import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import has from '@dojo/framework/core/has';
import { Button } from '@dojo/widgets/button';
import { Checkbox } from '@dojo/widgets/checkbox';
import { createNlsMiddleware, userstate, lccstate, recommended, quizmap } from '@holmescorp/lcc-state';
import { publish } from '@holmescorp/pub-sub';
import { numeral } from '@holmescorp/numeral';
import { resourceDitabookSelector } from '@holmescorp/resource-parser';
import { Icon } from '@holmescorp/base-widgets/icon';
import { AssessProgressViz } from '@holmescorp/prepost-viz';
import { Keys } from '@holmescorp/common';
import { YouAreHere } from './you-are-here';
import css from '../css/assess-view.m.css';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { svc } from '../support/svc';
const nls = createNlsMiddleware('ditabook');
const settings = createNlsMiddleware('settings');
const icache = createICacheMiddleware();
const factory = create({
    icache,
    theme,
    nls,
    userstate,
    lccstate,
    recommended,
    settings
}).properties();
export const AssessView = factory(({ properties, middleware: { icache, theme, nls, userstate, lccstate, recommended, settings } }) => {
    const themeCss = theme.classes(css);
    const { switchView } = properties();
    const { messages: ditabookMessages } = nls();
    const { assess_button = '[ditabook] assess_button', remind_me_later = '[ditabook] remind_me_later', begin_studies = '[ditabook] begin_studies', retake_assessment = '[ditabook] retake_assessment' } = ditabookMessages;
    const { messages: settingsMessages } = settings();
    const { show_remind = 'false', is_demo = false } = settingsMessages;
    const checked = icache.getOrSet('checked', false);
    const hasAssess = recommended.hasAssessment();
    const isAssessOnly = has('use-alternate-assess-view');
    const quizpath = userstate.get(userstate.path('quizPattern'));
    const pathid = quizpath && quizpath.length > 0 ? settingsMessages[`path-${quizpath}`] || '' : '';
    const { quiz: assessQuiz } = quizmap.get('assessment') || quizmap.get('pre-test') || {};
    const lockIcon = is_demo
        ? v('span', { classes: [themeCss.lockIcon] }, [w(Icon, { faname: 'lock' })])
        : isAssessOnly
            ? w(Icon, { faname: 'arrow-right' })
            : null;
    const quizState = icache.getOrSet('quiz', async () => {
        if (!isAssessOnly) {
            return;
        }
        const quizPattern = userstate.get(userstate.path('quizPattern'));
        const quizPatterns = lccstate.get(lccstate.path('quizpatterns'));
        const singleQuizPattern = Object.keys(quizPatterns).length <= 1;
        const hasQuizPattern = singleQuizPattern || (quizpath !== undefined && quizpath !== null);
        const isBasicQuiz = quizpath === '' || singleQuizPattern;
        const quizid = isBasicQuiz
            ? `${assessQuiz}`
            : hasQuizPattern
                ? `${assessQuiz}-${quizPattern}`
                : `${assessQuiz}`;
        const site = window.hcstatic.portal_site;
        const state = await svc.quiz_state({ site, quizids: [quizid] });
        return state[quizid];
    });
    const assess_button_label = quizState?.combined_status?.active ? 'Continue' : assess_button;
    const assessPending = () => {
        return [
            w(resourceDitabookSelector, {
                topic: 'assess_invite',
                key: 'assess-invite',
                classes: [themeCss.about],
                formatting: { pathid }
            }),
            v('div', { classes: [themeCss.button] }, [
                w(Button, {
                    onClick: () => {
                        if (!is_demo) {
                            publish('site/assessment', assessQuiz);
                        }
                    },
                    iconPosition: isAssessOnly ? 'after' : 'before'
                }, [
                    {
                        icon: lockIcon,
                        label: assess_button_label
                    }
                ])
            ]),
            show_remind === 'true'
                ? v('div', { classes: [themeCss.remind] }, [
                    w(Checkbox, {
                        checked,
                        onValue: (checked) => {
                            icache.set('checked', checked);
                        }
                    }, [remind_me_later])
                ])
                : null
        ];
    };
    const assessComplete = () => {
        const { get, path } = userstate;
        const { extra: overallscore = '0%' } = get(path('toolstate', 'assessment')) || { extra: '0%' };
        const scores = recommended.assessResults();
        const header = [
            w(resourceDitabookSelector, {
                topic: 'assess_completed',
                key: 'about-assess',
                xqa: 'assess-completed-score',
                classes: [themeCss.about],
                formatting: { pathid, percent: overallscore }
            }),
            w(resourceDitabookSelector, {
                topic: 'score_block_title',
                key: 'score-title',
                classes: [themeCss.scoreBlockTitle],
                formatting: { pathid }
            })
        ];
        const defaultContent = [
            v('div', {
                key: 'score-block',
                role: 'list',
                classes: [themeCss.scoreBlock],
                xqa: 'assess-score-block'
            }, [
                v('dl', {
                    role: 'listitem',
                    classes: [themeCss.scores]
                }, scores
                    .map((score) => {
                    return [
                        v('dt', { classes: [themeCss.scoreTitle], xqa: 'assess-score-title' }, [
                            `${score.title}`
                        ]),
                        v('dd', { classes: [themeCss.scoreValue], xqa: 'assess-score-value' }, [
                            `${numeral(score.percent / 100.0).format('0%')}`
                        ])
                    ];
                })
                    .flat())
            ]),
            v('div', { classes: [themeCss.button] }, [
                w(Button, {
                    onClick: () => {
                        publish('active-view', 'study');
                    }
                }, [begin_studies])
            ]),
            v('div', { classes: [themeCss.remind] }, [
                v('div', {
                    role: 'button',
                    tabIndex: 0,
                    classes: [themeCss.retake],
                    onclick: (event) => {
                        event.stopPropagation();
                        publish('site/assessment', assessQuiz);
                    },
                    onkeydown: (event) => {
                        switch (event.key) {
                            case Keys.Enter:
                            case Keys.Space:
                            case Keys.Spacebar:
                                event.preventDefault();
                                publish('site/assessment', assessQuiz);
                        }
                    }
                }, [retake_assessment])
            ])
        ];
        return !isAssessOnly ? [...header, ...defaultContent] : [...header, w(AssessProgressViz, {})];
    };
    const assessKids = hasAssess ? assessComplete() : assessPending();
    return v('div', { key: 'assess-view', classes: [themeCss.root], xqa: 'view assess' }, [
        !isAssessOnly ? w(YouAreHere, { active: 'assess', switchView }) : null,
        v('div', {
            id: 'tabPanelAssess',
            ...formatAriaProperties({
                labelledby: 'assessTab'
            }),
            classes: [themeCss.content]
        }, [
            ...assessKids,
            w(resourceDitabookSelector, {
                topic: 'assess_extra',
                key: 'extra-assess',
                classes: [themeCss.assessExtra]
            })
        ])
    ]);
});
